/**
 * The metric charts' shared tooltip: its chrome, its markup, and the helpers that
 * read a server-formatted value back apart to build the goal footer.
 *
 * The metric view screen's full-size chart and the scorecard drawer's sparkline
 * present a metric identically through renderTooltipHtml(), so the markup cannot
 * drift between them. Whatever the shared Superset bundle draws (the drawer, and the
 * metric screen's line, bars, pie, donut and gauge) hands its tooltip Superset's
 * params, so it formats through supersetTooltipFormatter() or, for a pie,
 * supersetSliceTooltipFormatter() below. The shapes the metric screen still draws with
 * the app's own echarts run their own formatter loop over that option tree
 * (chart-options.js).
 *
 * Two constraints on this file:
 *
 *  - It must never import echarts. The drawer imports it statically and the drawer
 *    is reachable from app.js, so a charting import here would pull echarts into
 *    the initial bundle on every page and undo the charts' lazy loading.
 *  - It must stay a `.js` under resources/js/, because tailwind.config.js scans
 *    "./resources/js/**\/*.js" and the swatch classes below are Tailwind
 *    arbitrary-value classes that only compile when the scanner sees them as
 *    literal strings. A `.ts` extension is not in the content globs, and
 *    interpolating a hex constant into the class compiles to nothing.
 */

import { AXIS_POINTER_COLOR } from './palette';
import { periodFormatter } from './superset-chart-describe';
import { isFiniteNumber } from './cartesian-chrome';

// The metric screen's echarts series names, shared by its series definitions, the tooltip legend
// keys and its formatter's lookups so a rename can't silently desync them (a mismatch just drops the
// row from the tooltip, with no error). They also name the three roles a metric's tooltip row plays,
// whichever chart drew it.
export const SERIES = { MAIN: 'main', PROJECTED: 'Projected', GOAL: 'Goal' };

// Tooltip rows, keyed by series name. Each swatch is drawn in its own line's style — solid vs
// dashed, brand vs grey — so a reader can tell which line a value belongs to when the goal and
// projected lines sit close to the actual one. A chart that inks its lines in the workspace's
// palette hands each row the colour its line was drawn in, and the swatch keeps its shape in that
// colour (see renderTooltipHtml).
export const TOOLTIP_LEGEND = {
    [SERIES.MAIN]: {
        label: 'Actual',
        swatch: 'inline-block w-3.5 shrink-0 border-t-[3px] border-solid border-[#15526e]',
    },
    [SERIES.PROJECTED]: {
        label: 'Projected',
        swatch: 'inline-block w-3.5 shrink-0 border-t-[3px] border-dashed border-[#15526e]',
    },
    [SERIES.GOAL]: {
        label: 'Goal',
        // Kept in step with GOAL_LINE_COLOR (#6B7280) so the swatch is the line, not an
        // approximation of it — spelled literally because Tailwind's scanner only emits
        // arbitrary values it can see as literal strings.
        swatch: 'inline-block w-3.5 shrink-0 border-t-2 border-dashed border-[#6b7280]',
    },
};

/**
 * The tooltip's visual shell, minus trigger and formatter — those differ per chart
 * (an axis trigger for a time series, an item trigger for a pie).
 *
 * `confine` is deliberately absent: on the drawer it would pen the tooltip inside a
 * 128px-tall chart box. The tooltip renders inside the chart container and sits
 * above surrounding content via its z-index.
 */
export const TOOLTIP_CHROME = {
    backgroundColor: '#ffffff',
    borderColor: 'rgba(16, 40, 48, 0.08)',
    borderWidth: 1,
    borderRadius: 8,
    padding: [8, 10],
    textStyle: { fontSize: 12 },
    extraCssText: 'z-index: 9999; box-shadow: 0 8px 24px -6px rgba(16, 40, 48, 0.18);',
    axisPointer: {
        type: 'line',
        lineStyle: { color: AXIS_POINTER_COLOR, width: 1, type: [4, 4] },
    },
};

// Values reach the tooltip as pre-formatted strings; escape before interpolating into its markup.
export const escapeHtml = (value) => String(value).replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])
);

/**
 * The workspace's number separators, mirroring App\Enums\MetricSeparatorOption. The server formats
 * every value with these (MetricService::formatValue), so a parser that assumed en-US would read
 * "70.000,00" as 70 and mangle the derived scale by 1000x. Unknown/missing keys fall back to the
 * enum's own default arm (comma thousands, dot decimal) — MetricChartPayloadBuilder applies the same
 * fallback server-side, so the two halves always agree on notation.
 */
const SEPARATORS = {
    thousands_comma_decimal_dot: { group: ',', decimal: '.' },
    thousands_period_decimal_comma: { group: '.', decimal: ',' },
    thousands_space_decimal_comma: { group: ' ', decimal: ',' },
};
const DEFAULT_SEPARATOR = SEPARATORS.thousands_comma_decimal_dot;

const separatorsFor = (key) => SEPARATORS[key] || DEFAULT_SEPARATOR;

const escapeRegExp = (char) => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Split a server-formatted value ("192h", "$1,240", "2.0s", "70.000,00 €") into its parts.
 *
 * The gap to goal is a number the server never formatted for us, and there is no JS port of
 * MetricService::formatValue to call — so rather than reimplement unit scaling here (and let it
 * drift), we read the server's own decisions back off the goal's formatted string: its prefix,
 * suffix, decimal places, and the display-per-raw scale implied by pairing it with the raw goal.
 * The separators must be the workspace's, not en-US, or the number is truncated at the first group
 * separator and the scale comes out wrong. Returns null when no scale can be derived, in which case
 * the footer is simply omitted.
 */
const parseFormattedValue = (formatted, separatorKey) => {
    const { group, decimal } = separatorsFor(separatorKey);
    const g = escapeRegExp(group);
    const d = escapeRegExp(decimal);

    // prefix (non-numeric) | number (digits + group/decimal separators) | suffix.
    // The number must end in at least one digit — an all-optional group would match empty at
    // position 0, letting the lazy prefix capture nothing and the number never form (e.g. "$1,240").
    const pattern = new RegExp(`^\\s*([^0-9\\-+${g}${d}]*?)\\s*(-?[\\d${g}]*${d}?\\d+)\\s*(.*?)\\s*$`);
    const match = String(formatted).match(pattern);
    if (!match) return null;

    // Strip group separators, then normalise the decimal mark so parseFloat can read it.
    const digits = match[2].split(group).join('').replace(decimal, '.');
    const value = parseFloat(digits);
    if (!Number.isFinite(value) || value === 0) return null;

    return {
        prefix: match[1] || '',
        suffix: match[3] || '',
        value,
        decimals: Math.min((digits.split('.')[1] || '').length, 2),
        separatorKey,
    };
};

const formatWithUnit = (value, unit) => {
    const { group, decimal } = separatorsFor(unit.separatorKey);
    // Format in a known locale, then swap in the workspace's separators — so the emitted gap wears
    // the same notation the server used for the goal it is being compared against.
    const enUs = value.toLocaleString('en-US', {
        minimumFractionDigits: unit.decimals,
        maximumFractionDigits: unit.decimals,
    });
    const [whole, fraction] = enUs.split('.');
    const grouped = whole.split(',').join(group);

    return unit.prefix + (fraction ? grouped + decimal + fraction : grouped) + unit.suffix;
};

/**
 * The tooltip's closing line: how far the actual value sits from its goal, and how much of the
 * goal it covers. `higherIsBetter` decides the tone (met = green, missed = red) independently of
 * the arrow, which always reports direction — a metric that should stay low is *good* below goal.
 */
const buildGoalFooter = (actualRaw, goal) => {
    if (!Number.isFinite(actualRaw) || !Number.isFinite(goal.raw) || goal.raw === 0) return '';

    const unit = parseFormattedValue(goal.formatted, goal.separatorKey);
    if (!unit) return '';

    const onTarget = goal.higherIsBetter ? actualRaw >= goal.raw : actualRaw <= goal.raw;
    const percent = Math.round((actualRaw / goal.raw) * 100);
    // "reached" reads wrong for a metric you want to keep low (95% of a latency budget is not an
    // achievement), so lower-is-better metrics report share of goal instead.
    const share = `${percent}% ${goal.higherIsBetter ? 'reached' : 'of goal'}`;
    // Met/missed in words: the arrow and "above/below goal" report direction, which is not the same
    // thing — below goal is good for a lower-is-better metric. Without this the outcome would be
    // carried by the green/red alone (WCAG 1.4.1).
    const status = onTarget ? 'goal met' : 'goal missed';

    const gapRaw = Math.abs(goal.raw - actualRaw);

    // red-600/green-600, not red-500: on white, red-500 is 3.48:1 — under AA for 11px text.
    let lead, tone, trail;
    if (gapRaw === 0) {
        lead = 'On goal';
        tone = 'text-green-600';
        trail = `${share} &middot; ${status}`;
    } else {
        const isAbove = actualRaw > goal.raw;
        const direction = isAbove ? 'above goal' : 'below goal';
        const arrow = isAbove ? '&uarr;' : '&darr;';
        tone = onTarget ? 'text-green-600' : 'text-red-600';

        // A gap finer than the metric's own display precision formats to a bare "0.0s", which reads
        // as sloppy rather than precise. Drop it and let the direction and share carry the line.
        const gapDisplay = gapRaw * (unit.value / goal.raw);
        const gapRoundsToZero = Number(gapDisplay.toFixed(unit.decimals)) === 0;

        lead = gapRoundsToZero
            ? `${arrow} ${direction}`
            : `${arrow} ${escapeHtml(formatWithUnit(gapDisplay, unit))}`;
        trail = gapRoundsToZero
            ? `&middot; ${share} &middot; ${status}`
            : `${direction} &middot; ${share} &middot; ${status}`;
    }

    // The wrapper chrome is written once for both the on-goal and off-goal cases.
    return `<div class="mt-2 flex items-center gap-1.5 whitespace-nowrap border-t border-gray-100 pt-2 text-[11px] leading-none">`
        + `<span class="font-semibold ${tone}">${lead}</span>`
        + `<span class="text-gray-500">${trail}</span>`
        + `</div>`;
};

// A colour ECharts reports as a plain CSS colour. Anything else (a gradient object, or a string that
// could close the attribute) leaves the swatch in its class's own colour.
const CSS_COLOR = /^(#[0-9a-f]{3,8}|(rgb|hsl)a?\([\d\s.,%/]+\)|[a-z]+)$/i;

// The colour is a runtime value (the workspace's palette, read off the drawn series), which no
// Tailwind class can name, so it is the one part of the swatch set inline.
const swatchHtml = (legend) => (typeof legend.color === 'string' && CSS_COLOR.test(legend.color)
    ? `<span class="${legend.swatch}" style="border-top-color: ${escapeHtml(legend.color)}"></span>`
    : `<span class="${legend.swatch}"></span>`);

/**
 * The tooltip's markup.
 *
 * The actual value is what the reader came for; goal and projected are reference
 * context. Actual is pulled out so it can anchor the readout, sized and inked above
 * the others, while the context lines stay deliberately quiet. Rows read highest
 * value first.
 *
 * gray-500/600 rather than gray-400: on the tooltip's white ground gray-400 lands at
 * 2.45:1, under WCAG AA's 4.5:1 for text this size. The label stays recessive against
 * the value through weight and colour depth instead.
 *
 * A legend carries a `swatch` class drawn in its line's style. It may also carry a
 * `color`: the colour the chart drew that line in, for a chart whose colours come
 * from the workspace's palette rather than from this file. The swatch keeps its
 * shape, solid or dashed, and takes that colour, so it still looks like its line.
 *
 * @param {object}   args
 * @param {string}   args.title        Period name, already plain text.
 * @param {?object}  args.actual       { sortValue, legend, display } for the metric's own
 *                                     value. sortValue is the number the goal footer
 *                                     compares against goalContext.raw, so the two must be
 *                                     on one scale — omit it and buildGoalFooter's
 *                                     is-finite guard silently drops the footer, with no
 *                                     error and no fallback text.
 * @param {object[]} args.context      Reference rows, each { sortValue, legend, display }.
 * @param {?object}  args.goalContext  { raw, formatted, separatorKey, higherIsBetter } or null.
 * @param {?string}  args.hint         A last, quiet line saying what a click on the hovered period
 *                                     does (a report card's "Click to open details"), plain text;
 *                                     null prints none, which is every other surface.
 */
export const renderTooltipHtml = ({ title, actual, context = [], goalContext = null, hint = null }) => {
    if (!actual && context.length === 0) return '';

    const sorted = [...context].sort((a, b) => b.sortValue - a.sortValue);

    const contextHtml = sorted.map((r) => `
        <div class="flex items-center gap-2 leading-none">
          ${swatchHtml(r.legend)}
          <span class="text-[11px] text-gray-500">${escapeHtml(r.legend.label)}</span>
          <span class="ml-auto pl-5 text-[12px] font-medium text-gray-600 tabular-nums">${escapeHtml(r.display)}</span>
        </div>`).join('');

    const actualHtml = actual ? `
        <div class="flex items-center gap-2 leading-none">
          ${swatchHtml(actual.legend)}
          <span class="text-[11px] font-semibold text-blink">${escapeHtml(actual.legend.label)}</span>
          <span class="ml-auto pl-5 text-[15px] font-bold text-blink tabular-nums">${escapeHtml(actual.display)}</span>
        </div>` : '';

    const footerHtml = (actual && goalContext)
        ? buildGoalFooter(actual.sortValue, goalContext)
        : '';

    // gray-500 on white is 4.83:1, AA for 11px text.
    const hintHtml = typeof hint === 'string' && hint !== ''
        ? `<div class="mt-2 whitespace-nowrap text-[11px] leading-none text-gray-500">${escapeHtml(hint)}</div>`
        : '';

    return `<div class="mb-2 text-[11px] font-medium leading-none text-gray-500">${escapeHtml(title)}</div>`
        + `<div class="flex flex-col gap-2">${contextHtml}${actualHtml}</div>`
        + footerHtml
        + hintHtml;
};

/**
 * renderTooltipHtml() over the params of a chart the Superset bundle drew, for a surface that
 * passes it through the chart host as `{...TOOLTIP_CHROME, trigger: 'axis', formatter}`
 * (blink-superset-bundle.md).
 *
 * Superset's params are not an app-built option tree's, and each difference prints a wrong row
 * with no error if it is missed:
 *  - `seriesName` is the contract's series label, and Superset orders the series itself, so a row
 *    is found by its label, never by its index.
 *  - `value` is `[instant, y]`: the period's start at UTC midnight in epoch ms, and the value on
 *    the chart's drawn scale, which may be the reading divided by a thousand. A chart drawn over
 *    its periods' own labels, as the metric view's are, hands `[label, y]` instead. So a row prints
 *    the server's string for its series and period, never the number, and the goal footer is
 *    handed the goal on that same scale.
 *  - A series with no reading at the hovered period is still handed over, with a null y. It gets
 *    no row, and neither does a point at any other period.
 *
 * The title names the hovered period through the host's own periodFormatter, in UTC, so it reads as
 * the host's sr-only table does when the two are handed the same `grain` and `periods`: the
 * surface's own name for the instant where `periods` has one, else the period at `grain` (a month
 * as "September 2026"), else the day. A period drawn over its label is titled `periods`' name for
 * that label, else the label itself. A monthly reading titled by its first day ("Sep 1, 2026")
 * puts one month's figure under one day's name. Each swatch is its line's own (TOOLTIP_LEGEND: solid for
 * the actual, dashed for the projection and the goal) in the colour ECharts reports it drew that
 * series in. ECharts' round `marker` is not used: it would drop the solid/dashed cue.
 *
 * @param {object}  chart
 * @param {object}  chart.labels    { actual, projected, goal }: the label each role is drawn under;
 *                                  projected and goal may be null.
 * @param {object}  chart.displays  label => instant (or period label) => the server's string for that point.
 * @param {?object} chart.goal      { value, formatted, higherIsBetter, separatorKey } while the goal
 *                                  is drawn, else null, which leaves the footer out. `value` is on
 *                                  the drawn scale.
 * @param {object}  [options]
 * @param {string}  [options.locale]  The page's language, for the title.
 * @param {object}  [options.text]    { actual, projected, goal } row names; TOOLTIP_LEGEND's otherwise.
 * @param {?string} [options.grain]   The chart's time grain, its contract's `formData.timeGrainSqla`
 *                                    ('P1D', 'P1W', 'P1M', 'P3M', 'P1Y'), which names a period with no
 *                                    entry in `periods`. Omitted, such a period is named by its date.
 * @param {?Object<number|string, string>} [options.periods]  x value => the surface's own name for
 *                                    the period drawn there, keyed by the x value the formatter is
 *                                    handed (the period's start at UTC midnight, in epoch ms, or its
 *                                    label), the same shape as the chart host's `periods`. Wins over `grain`.
 * @param {boolean} [options.horizontal]  The chart lays its periods up the side, as a horizontal bar
 *                                    chart does, so each point arrives as `[y, instant]` (or `[y, label]`).
 * @param {?Function} [options.hint]  (period) => the hint line for the hovered period (its instant,
 *                                    or its label), or null for none: what a click there does, which
 *                                    only the surface knows (a report card's periods that a click
 *                                    opens). Asked on every hover. Omitted, no tooltip has one.
 * @returns {Function} An ECharts formatter for the axis trigger's params.
 */
export const supersetTooltipFormatter = ({ labels = {}, displays = {}, goal = null }, { locale, text = {}, grain = null, periods = null, horizontal = false, hint = null } = {}) => {
    const roles = new Map([
        [labels.actual, { series: SERIES.MAIN, name: text.actual }],
        [labels.projected, { series: SERIES.PROJECTED, name: text.projected }],
        [labels.goal, { series: SERIES.GOAL, name: text.goal }],
    ].filter(([label]) => typeof label === 'string'));

    const instants = [...new Set(Object.values(displays).flatMap((points) => Object.keys(points ?? {}).map(Number)))].filter(isFiniteNumber);
    const formatPeriod = periodFormatter(instants, locale, grain, periods);
    const title = (at) => (typeof at === 'string' ? (periods?.[at] ?? at) : formatPeriod(at));
    const goalContext = goal
        ? { raw: goal.value, formatted: goal.formatted, higherIsBetter: goal.higherIsBetter, separatorKey: goal.separatorKey }
        : null;

    return (params) => {
        const points = (Array.isArray(params) ? params : [params])
            .filter(Boolean)
            .map((item) => ({
                item,
                point: Array.isArray(item.value)
                    ? (horizontal ? [item.value[1], item.value[0]] : item.value)
                    : [item.axisValue, item.value],
            }));
        // An instant on a time axis, or the period's own label on a category axis.
        const period = points.map(({ point }) => point[0]).find((x) => isFiniteNumber(x) || (typeof x === 'string' && x !== ''));

        if (period === undefined) return '';

        let actual = null;
        const context = [];

        points.forEach(({ item, point: [at, value] }) => {
            const role = roles.get(item.seriesName);
            const display = displays[item.seriesName]?.[at];

            if (!role || at !== period || !isFiniteNumber(value) || display === undefined || display === null) return;

            const row = {
                sortValue: value,
                legend: {
                    ...TOOLTIP_LEGEND[role.series],
                    label: role.name ?? TOOLTIP_LEGEND[role.series].label,
                    color: item.color,
                },
                display,
            };

            if (role.series === SERIES.MAIN) {
                actual = row;
            } else {
                context.push(row);
            }
        });

        return renderTooltipHtml({ title: title(period), actual, context, goalContext, hint: typeof hint === 'function' ? hint(period) : null });
    };
};

/**
 * renderTooltipHtml() over a pie or donut the Superset bundle drew, for the item trigger: the
 * slice's name as the title and one Actual row, its value and its share of the drawn whole.
 *
 * The value is the server's string for the slice, found by the slice's name, never Superset's
 * number, which is on the chart's drawn scale. The share is `formatShare(value, total)` over the
 * drawn values, the same function the host's sr-only table prints shares with, so the two never
 * disagree about a slice. Swatched in the colour ECharts reports it drew the slice in.
 *
 * @param {object}   chart
 * @param {object}   chart.displays     slice name => the server's string for its value.
 * @param {number}   chart.total        The sum of the drawn slices, on the drawn scale.
 * @param {Function} chart.formatShare  (value, total) => the share as the page prints it.
 * @param {object}   [options]
 * @param {object}   [options.text]     { actual } row name; TOOLTIP_LEGEND's otherwise.
 * @returns {Function} An ECharts formatter for the item trigger's params.
 */
export const supersetSliceTooltipFormatter = ({ displays = {}, total = 0, formatShare }, { text = {} } = {}) => (params) => {
    const display = displays[params?.name];

    if (!params || display === undefined || display === null || !isFiniteNumber(params.value)) return '';

    return renderTooltipHtml({
        title: String(params.name),
        actual: {
            sortValue: params.value,
            legend: {
                ...TOOLTIP_LEGEND[SERIES.MAIN],
                label: text.actual ?? TOOLTIP_LEGEND[SERIES.MAIN].label,
                color: params.color,
            },
            display: `${display} (${formatShare(params.value, total)})`,
        },
        context: [],
        goalContext: null,
    });
};
